import { PROPERTY_STATUS } from '@ibe/constants';
import { z } from 'zod';

import { isKnownTimezone } from '../../../lib/forms/timezones.js';

/**
 * The Properties module's contracts.
 *
 * These schemas are the source of truth for the shapes this module reads and
 * writes. Types come from `z.infer`, never hand-written, so a schema and its
 * type cannot drift apart.
 *
 * Every limit below is the one the service enforces (`packages/property`), so
 * an over-long name is a sentence beside the input rather than a round trip
 * and a 422. The server still validates — this is a courtesy, never the
 * enforcement.
 */

/** Carried over from the property service. Tightening it would reject codes
 *  already in the database. */
const CODE_PATTERN = /^[A-Z0-9-]{2,20}$/u;
/** `numeric(10, 7)`: up to three whole digits and seven decimal places. */
const COORDINATE_PATTERN = /^-?\d{1,3}(?:\.\d{1,7})?$/u;
/** `14:00` or `14:00:00`. What an `<input type="time">` produces, either way. */
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/u;

const MAX_NAME = 200;
const MAX_DISPLAY_NAME = 255;
const MAX_DESCRIPTION = 4000;
const MAX_PROPERTY_TYPE = 40;
const MAX_EMAIL = 254;
const MAX_PHONE = 32;
const MAX_PHONE_CODE = 10;
const MAX_WEBSITE = 512;
const MAX_ABN = 20;
const MAX_ADDRESS_LINE = 255;
const MAX_POST_CODE = 32;
const MAX_PMS_ID = 40;
const MAX_URL = 512;
const MAX_ALT_TEXT = 255;
const MAX_STATE_CODE = 40;
const CURRENCY_LENGTH = 3;
const COUNTRY_CODE_LENGTH = 2;

const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

/**
 * One row in the properties list.
 *
 * **Only `id` is guaranteed.** The list sends `?fields=` derived from the
 * columns the reader has chosen and the service honours it strictly, so a
 * field behind a hidden column is genuinely absent from the response.
 * Declaring any of the others required would make the whole list fail to parse
 * the moment someone hides a column — which is exactly what happened to the
 * tenants list before its row schema was relaxed the same way.
 *
 * `id` is exempt because `fieldsForColumns` always requests it: `rowKey` and
 * every row link need it whatever else was selected.
 */
export const propertyRow = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  displayName: z.string().nullable().optional(),
  status: z.string().optional(),
  propertyType: z.string().nullable().optional(),
  starRating: z.number().nullable().optional(),
  logo: z.string().nullable().optional(),
  timezone: z.string().optional(),
  currency: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  stateCode: z.string().nullable().optional(),
  cityCode: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type PropertyRow = z.infer<typeof propertyRow>;

/**
 * The full property, as the detail screen and the edit form read it.
 *
 * Required here rather than optional, unlike the row above: the detail
 * endpoint is called without `?fields=` and therefore always projects
 * everything, so a missing key is a contract change worth failing on.
 * `nullable` marks the columns that genuinely hold null — a different answer
 * from "the server did not send this field".
 */
export const propertyDetail = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  description: z.string().nullable(),
  status: z.string(),
  propertyType: z.string().nullable(),
  starRating: z.number().nullable(),
  logo: z.string().nullable(),
  logoAltText: z.string().nullable().optional(),
  timezone: z.string(),
  currency: z.string().nullable(),
  countryCode: z.string().nullable(),
  stateCode: z.string().nullable(),
  cityCode: z.string().nullable(),
  city: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  phoneCode: z.string().nullable(),
  mobile: z.string().nullable(),
  website: z.string().nullable(),
  abn: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  addressLine4: z.string().nullable(),
  postCode: z.string().nullable(),
  // Strings, not numbers. The column is `numeric(10, 7)` and the driver hands
  // it over as text so no coordinate is rounded on the way to a screen.
  latitude: z.string(),
  longitude: z.string(),
  defaultCheckInTime: z.string().nullable(),
  defaultCheckOutTime: z.string().nullable(),
  pmsConfigured: z.boolean(),
  pmsAdapterId: z.string().nullable(),
  pmsPropertyId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Echoed back so the edit form can round-trip it on save — see
  // `useUpdateProperty`. It is the optimistic lock, not decoration.
  version: z.number(),
});

export type PropertyDetail = z.infer<typeof propertyDetail>;

/** The statuses a property may be moved between, in the order they read. */
export const PROPERTY_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: PROPERTY_STATUS.ACTIVE, label: 'Active' },
  { value: PROPERTY_STATUS.DRAFT, label: 'Draft' },
  { value: PROPERTY_STATUS.INACTIVE, label: 'Inactive' },
  { value: PROPERTY_STATUS.MAINTENANCE, label: 'Maintenance' },
  { value: PROPERTY_STATUS.ARCHIVED, label: 'Archived' },
];

const statusValues = PROPERTY_STATUS_OPTIONS.map((option) => option.value) as [string, ...string[]];

/** Star ratings, as the strings a `<select>` holds. */
export const STAR_RATING_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: '1 star' },
  { value: '2', label: '2 stars' },
  { value: '3', label: '3 stars' },
  { value: '4', label: '4 stars' },
  { value: '5', label: '5 stars' },
];

/** Property types a property may be assigned. */
export const PROPERTY_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'RESORT', label: 'Resort' },
  { value: 'APARTMENT', label: 'Apartment' },
];

/** An optional free-text field: absent is the empty string, never null. */
function optionalText(max: number, tooLong: string): z.ZodString {
  return z.string().trim().max(max, tooLong);
}

/**
 * A coordinate, as typed.
 *
 * Kept a string end to end. Parsing it to a `number` here and formatting it
 * back would quietly re-round the seventh decimal place, which is the one the
 * column was given seven digits to preserve.
 */
function coordinate(min: number, max: number, field: string): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .min(1, `${field} is required — it is what places this property on a map`)
    .regex(
      COORDINATE_PATTERN,
      `${field} must be a number, such as ${field === 'Latitude' ? '-37.8136' : '144.9631'}`,
    )
    .refine(
      (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= min && parsed <= max;
      },
      `${field} must be between ${String(min)} and ${String(max)}`,
    );
}

function optionalTime(label: string): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .refine(
      (value) => value === '' || TIME_PATTERN.test(value),
      `${label} must be a time of day, such as 14:00`,
    );
}

/**
 * What the create and edit forms collect.
 *
 * One schema for both, because they are the same fields under the same rules;
 * what differs is which of them the service will accept, and that is the
 * payload builder's job rather than a second schema to keep in step. Every
 * value is a string: a controlled input cannot hold `null`, and `starRating`
 * is the string a select holds rather than the number the API takes.
 */
export const propertyFormInput = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'A property needs a name')
    .max(MAX_NAME, `Keep the name under ${String(MAX_NAME)} characters`),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(CODE_PATTERN, 'Two to twenty letters, digits or hyphens — for example GRAND-01'),
  displayName: optionalText(MAX_DISPLAY_NAME, 'That display name is too long'),
  description: optionalText(MAX_DESCRIPTION, 'That description is too long'),
  propertyType: z
    .string()
    .trim()
    .toUpperCase()
    .max(MAX_PROPERTY_TYPE, 'That property type is too long'),
  starRating: z.string(),
  logo: z
    .union([z.literal(''), z.url('Enter a full URL, starting with https://')])
    .refine((value) => value.length <= MAX_URL, 'That URL is too long'),
  logoAltText: optionalText(MAX_ALT_TEXT, 'That alt text is too long'),
  timezone: z
    .string()
    .trim()
    .min(1, 'A property needs a time zone — it is what its check-in time means')
    .refine(isKnownTimezone, 'Choose a time zone from the list'),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .length(CURRENCY_LENGTH, 'A three-letter ISO currency code, such as AUD'),
  countryCode: z.union([
    z.literal(''),
    z.string().trim().toUpperCase().length(COUNTRY_CODE_LENGTH),
  ]),
  stateCode: optionalText(MAX_STATE_CODE, 'That state code is too long'),
  cityCode: optionalText(MAX_STATE_CODE, 'That city code is too long'),
  email: z
    .union([z.literal(''), z.email('That does not look like an email address')])
    .refine((value) => value.length <= MAX_EMAIL, 'That email address is too long'),
  phone: optionalText(MAX_PHONE, 'That phone number is too long'),
  phoneCode: optionalText(MAX_PHONE_CODE, 'A dialling code such as +61'),
  mobile: optionalText(MAX_PHONE, 'That mobile number is too long'),
  website: z
    .union([z.literal(''), z.url('Enter a full URL, starting with https://')])
    .refine((value) => value.length <= MAX_WEBSITE, 'That URL is too long'),
  abn: optionalText(MAX_ABN, 'That business number is too long'),
  addressLine1: optionalText(MAX_ADDRESS_LINE, 'That address line is too long'),
  addressLine2: optionalText(MAX_ADDRESS_LINE, 'That address line is too long'),
  addressLine3: optionalText(MAX_ADDRESS_LINE, 'That address line is too long'),
  addressLine4: optionalText(MAX_ADDRESS_LINE, 'That address line is too long'),
  postCode: optionalText(MAX_POST_CODE, 'That postcode is too long'),
  latitude: coordinate(MIN_LATITUDE, MAX_LATITUDE, 'Latitude'),
  longitude: coordinate(MIN_LONGITUDE, MAX_LONGITUDE, 'Longitude'),
  defaultCheckInTime: optionalTime('Check-in time'),
  defaultCheckOutTime: optionalTime('Check-out time'),
  pmsConfigured: z.boolean(),
  pmsAdapterId: z.uuid().or(z.literal('')),
  pmsPropertyId: optionalText(MAX_PMS_ID, 'That property id is too long'),
  // Only the edit form renders it: a new property is always created in DRAFT,
  // by the service, so offering a status on the create screen would be a
  // control that silently does nothing.
  status: z.enum(statusValues),
});

export type PropertyFormInput = z.infer<typeof propertyFormInput>;

/** What the create endpoint returns. */
export const createdProperty = z.object({ id: z.string() });

export type CreatedProperty = z.infer<typeof createdProperty>;

/** Reference data: the vocabularies a property is written in. */
export { currencyOption, type CurrencyOption } from '../../../lib/reference/currencies.js';

export const namedCodeOption = z.object({ code: z.string(), name: z.string() });

export type NamedCodeOption = z.infer<typeof namedCodeOption>;

/** A country is a named code that also carries its dialling prefix. */
export const countryOption = namedCodeOption.extend({
  /** Null where the country has no calling code of its own. */
  phonePrefix: z.string().nullable(),
});

export type CountryOption = z.infer<typeof countryOption>;
